import React, { useState } from "react";
import { Send, Sparkles, Smartphone, Megaphone, X, Loader2, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import LockedFeatureCard from "@/components/admin/LockedFeatureCard";
import HowThisWorks from "@/components/admin/HowThisWorks";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

export default function Outbound() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e?.preventDefault();
        setSubmitting(true);
        try {
            await adminApi.post("/api/admin/beta/request", {
                feature: "outbound_campaigns",
                name: form.name,
                email: form.email,
                notes: form.notes,
            });
            setDone(true);
            toast.success("Request received — our team will be in touch within 1 business day.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit request");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setOpen(false);
        setDone(false);
        setForm({ name: "", email: "", notes: "" });
    };

    return (
        <div className="p-10 max-w-5xl" data-testid="page-outbound">
            <PageHeader
                eyebrow="Beta"
                title="Campaigns"
                description="Proactively reach guests on WhatsApp and other channels — promotions, reminders, re-engagement."
            />

            <LockedFeatureCard
                title="Outbound Campaigns"
                description="Send Priya-curated offers and reminders to your guest list via WhatsApp. Set audience filters, pick a template, schedule, and track."
                icon={Send}
                badge="Beta"
                actionLabel="Upgrade to unlock"
                testid="outbound-locked"
                onAction={() => setOpen(true)}
            />

            <div className="mt-8">
                <HowThisWorks
                    steps={[
                        "Create a campaign and choose your target audience (members, hotel-specific guests, etc.).",
                        "Select a WhatsApp message template approved by Meta.",
                        "Schedule and send the campaign, then track delivery, opens and replies in real time.",
                    ]}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                {[
                    {
                        icon: Megaphone,
                        title: "Reach proactively",
                        desc: "Member-only offers, festival promos, anniversary nudges.",
                    },
                    {
                        icon: Smartphone,
                        title: "WhatsApp first",
                        desc: "Reach guests on the channel they actually open.",
                    },
                    {
                        icon: Sparkles,
                        title: "Priya-curated",
                        desc: "Personalised at scale — guest name, hotel, history.",
                    },
                ].map((f) => (
                    <div key={f.title} className="bg-white border border-stone-200 rounded-xl p-5">
                        <f.icon size={20} className="text-orchid-700 mb-3" />
                        <div className="font-medium text-stone-900 text-sm">{f.title}</div>
                        <div className="text-xs text-stone-600 mt-1 leading-relaxed">{f.desc}</div>
                    </div>
                ))}
            </div>

            {open && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={reset}
                    data-testid="outbound-modal"
                >
                    <div
                        className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                                    Beta access
                                </div>
                                <h3 className="font-heading text-2xl text-orchid-700">
                                    Outbound Campaigns
                                </h3>
                            </div>
                            <button
                                onClick={reset}
                                className="text-stone-400 hover:text-stone-700"
                                data-testid="outbound-close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {done ? (
                            <div className="py-6 text-center" data-testid="outbound-done">
                                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-3">
                                    <CheckCircle2 size={26} />
                                </div>
                                <div className="font-medium text-stone-900">
                                    You&apos;re on the list
                                </div>
                                <p className="text-xs text-stone-500 mt-2 max-w-xs mx-auto">
                                    Our team will reach out to{" "}
                                    <span className="font-medium">{form.email || "you"}</span> within 1
                                    business day to set up Outbound Campaigns.
                                </p>
                                <button
                                    onClick={reset}
                                    className="mt-5 text-sm font-medium px-4 py-2 rounded-md border border-stone-200 hover:bg-stone-50"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit}>
                                <p className="text-xs text-stone-500 mb-5">
                                    Outbound is part of the Enterprise plan. Tell us a bit about your
                                    audience and we&apos;ll spin up the WhatsApp Business templates and
                                    your dedicated sender number.
                                </p>
                                <div className="space-y-3">
                                    <input
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({ ...form, name: e.target.value })
                                        }
                                        placeholder="Your name"
                                        data-testid="outbound-name"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                    />
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({ ...form, email: e.target.value })
                                        }
                                        placeholder="Email"
                                        data-testid="outbound-email"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                    />
                                    <textarea
                                        rows={3}
                                        value={form.notes}
                                        onChange={(e) =>
                                            setForm({ ...form, notes: e.target.value })
                                        }
                                        placeholder="Approx. audience size, campaign type, etc."
                                        data-testid="outbound-notes"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                    />
                                </div>
                                <div className="flex justify-end gap-2 mt-5">
                                    <button
                                        type="button"
                                        onClick={reset}
                                        className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="outbound-submit"
                                        className="text-sm font-medium px-4 py-2 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white disabled:opacity-50 inline-flex items-center gap-2"
                                    >
                                        {submitting ? (
                                            <Loader2 size={13} className="animate-spin" />
                                        ) : null}
                                        Request access
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
